import React from "react";
import { useDroppable } from "@dnd-kit/core";
import Typography from "../../../common/Typography";
import Button from "../../../common/Button";
import { CheckCircle, XCircle, X } from "lucide-react";

interface SequenceDropZoneProps {
  id: string;
  label: string;
  matchedItem: string | null;
  isCorrect: boolean;
  isIncorrect: boolean;
  isLocked: boolean;
  stepNumber: number;
  isChecking: boolean;
  isDisabled: boolean;
  // eslint-disable-next-line no-unused-vars
  onMoveToAvailable?: (itemId: string) => void;
  itemId?: string | null;
}

const SequenceDropZone: React.FC<SequenceDropZoneProps> = ({
  id,
  matchedItem,
  isCorrect,
  isIncorrect,
  stepNumber,
  isChecking,
  isDisabled,
  onMoveToAvailable,
  itemId,
}) => {
  const { setNodeRef, isOver } = useDroppable({
    id,
    disabled: isDisabled,
  });

  const getBorderColor = () => {
    if (isDisabled) {return "border-gray-200 bg-gray-100 opacity-50";}
    if (isCorrect) {return "border-green-500 bg-green-50";}
    if (isIncorrect) {return "border-red-500 bg-red-50";}
    if (matchedItem) {return "border-solid bg-white shadow-sm";}
    return "border-gray-300 bg-gray-50";
  };

  const getStepNumberColor = () => {
    if (isCorrect) {return "bg-green-500 text-white";}
    if (isIncorrect) {return "bg-red-500 text-white";}
    if (isDisabled) {return "bg-gray-300 text-gray-500";}
    return "bg-gray-200 text-gray-600";
  };

  return (
    <div
      ref={setNodeRef}
      data-testid="dnd-sequence-drop-zone"
      data-dnd-id={id}
      className={`min-h-[60px] p-4 border-2 border-dashed rounded-lg transition-all duration-200 mb-4 ${
        isDisabled
          ? getBorderColor()
          : isOver
            ? "border-blue-400 bg-blue-50"
            : getBorderColor()
      }`}
    >
      <div className="flex items-center gap-3">
        {/* Step Number */}
        <div
          className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium flex-shrink-0 ${getStepNumberColor()}`}
        >
          {stepNumber}
        </div>

        {/* Item Content */}
        <div className={`flex-1 ${matchedItem ? "bg-blue-50 rounded-md" : ""}`}>
          {matchedItem ? (
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <Typography variant="p" className="text-gray-800 pl-2">
                  {matchedItem}
                </Typography>
                {isCorrect && (
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                )}
                {isIncorrect && (
                  <XCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                )}
              </div>
              {/* Move to available button (only show before checking) */}
              {!isChecking && onMoveToAvailable && itemId && (
                <Button
                  variant="text"
                  size="small"
                  onClick={() => onMoveToAvailable(itemId)}
                  className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-full mr-1"
                >
                  <X className="w-4 h-4" />
                </Button>
              )}
            </div>
          ) : (
            <Typography variant="p" className="text-gray-500">
              Drop item here
            </Typography>
          )}
        </div>
      </div>
    </div>
  );
};

export default SequenceDropZone;
